/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, TextInput, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import type { OnboardingPickerItem } from '../../../onboarding/application/onboardingPickerService';
import type { OtherFeatureGroup, OtherFeatureOptionId } from '../../../onboarding/application/otherFeatureCatalog';
import { BusinessTypePickerGrid } from './BusinessTypePickerGrid';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import { OtherRubroStepPanel } from './OtherRubroStepPanel';
import { IdentityStepPanel } from './IdentityStepPanel';
import type { OnboardingLayout } from './useOnboardingLayout';
import type { OnboardingWizardStepId } from './onboardingWizardConfig';
import { GastronomyStockModeStepPanel } from './GastronomyStockModeStepPanel';
import type { GastronomyStockMode } from '../../../onboarding/application/gastronomyStockMode';

type Props = {
  currentStepId: OnboardingWizardStepId;
  /** Business-type step */
  pickerItems: readonly OnboardingPickerItem[];
  selectedPickerKey: string;
  onSelectPickerItem: (item: OnboardingPickerItem) => void;
  layout: OnboardingLayout;
  loadingTypes: boolean;
  typesLoadError?: string;
  /** Retry del fetch del picker tras typesLoadError. */
  onRetryPickerTypes?: () => void;
  submitting: boolean;
  /** Other-rubro step */
  otherFeatureGroups: readonly OtherFeatureGroup[];
  otherFeatureIds: readonly OtherFeatureOptionId[];
  onToggleOtherFeatureId: (id: OtherFeatureOptionId) => void;
  otherRubroLabel: string;
  onChangeOtherRubroLabel: (value: string) => void;
  otherRubroLabelInputRef?: React.RefObject<TextInput | null>;
  /** Identity step */
  businessName: string;
  onChangeBusinessName: (value: string) => void;
  ownerName?: string;
  onChangeOwnerName?: (value: string) => void;
  referralCode?: string;
  onChangeReferralCode?: (value: string) => void;
  showReferralCodeField?: boolean;
  businessNameInputRef?: React.RefObject<TextInput | null>;
  canProceed: boolean;
  onNext: () => void;
  gastronomyStockMode?: GastronomyStockMode;
  onSelectGastronomyStockMode?: (mode: GastronomyStockMode) => void;
};

/**
 * Render del cuerpo del paso actual del wizard (business-type / other-rubro /
 * identity). Recibe props derivadas del state machine y despacha al panel
 * correspondiente. Extraído de OrganizationSetupWizard para separar la
 * presentación de cada paso de la chrome del wizard y el state machine.
 */
export function OnboardingWizardSteps({
  currentStepId,
  pickerItems,
  selectedPickerKey,
  onSelectPickerItem,
  layout,
  loadingTypes,
  typesLoadError,
  onRetryPickerTypes,
  submitting,
  otherFeatureGroups,
  otherFeatureIds,
  onToggleOtherFeatureId,
  otherRubroLabel,
  onChangeOtherRubroLabel,
  otherRubroLabelInputRef,
  businessName,
  onChangeBusinessName,
  ownerName,
  onChangeOwnerName,
  referralCode,
  onChangeReferralCode,
  showReferralCodeField,
  businessNameInputRef,
  canProceed,
  onNext,
  gastronomyStockMode = 'B',
  onSelectGastronomyStockMode,
}: Props) {
  switch (currentStepId) {
    case 'business-type':
      return (
        <View style={styles.stepBody}>
          <BusinessTypePickerGrid
            businessTypes={pickerItems}
            selectedPickerKey={selectedPickerKey}
            onSelect={onSelectPickerItem}
            layout={layout}
            loading={loadingTypes}
            disabled={submitting}
            onRetry={onRetryPickerTypes}
          />
          {typesLoadError ? (
            <ModuleEmptyState
              variant="nest"
              showMascot={false}
              title={typesLoadError}
              secondaryAction={
                onRetryPickerTypes
                  ? { label: 'Reintentar', onPress: onRetryPickerTypes }
                  : undefined
              }
              testID="business-type-picker-load-error"
            />
          ) : null}
        </View>
      );
    case 'gastronomy-stock-mode':
      return (
        <GastronomyStockModeStepPanel
          selectedMode={gastronomyStockMode}
          onSelectMode={(mode) => onSelectGastronomyStockMode?.(mode)}
          disabled={submitting}
        />
      );
    case 'other-rubro':
      return (
        <OtherRubroStepPanel
          layout={layout}
          featureGroups={otherFeatureGroups}
          selectedFeatureIds={otherFeatureIds}
          onToggleFeature={onToggleOtherFeatureId}
          verticalLabel={otherRubroLabel}
          onChangeVerticalLabel={onChangeOtherRubroLabel}
          labelInputRef={otherRubroLabelInputRef}
          disabled={submitting}
        />
      );
    case 'identity':
      return (
        <IdentityStepPanel
          businessName={businessName}
          onChangeBusinessName={onChangeBusinessName}
          ownerName={ownerName}
          onChangeOwnerName={onChangeOwnerName}
          referralCode={referralCode}
          onChangeReferralCode={onChangeReferralCode}
          showReferralCodeField={showReferralCodeField}
          labelInputRef={businessNameInputRef}
          disabled={submitting}
          canProceed={canProceed}
          onSubmit={onNext}
        />
      );
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  stepBody: { gap: spacing.md },
});
